/**
 * The react-three-fiber root around the imperative fab.
 *
 * React owns MOUNTING; three objects stay imperative, created once, mutated
 * per-frame. The FabScene constructor builds the whole hall into the R3F
 * root scene exactly as it did into main.ts's scene; the player rig mounts
 * as a <primitive>; the player's own camera becomes R3F's default camera so
 * resize and aspect are handled for free.
 *
 * TWO RULES THIS FILE ENFORCES, both load-bearing:
 *
 * NO LIGHTS IN JSX, EVER. The visible light count is a constant per quality
 * tier (see FabScene.lightBudget) because three.js bakes that count into
 * every shader program - a re-render that mounted or unmounted a light would
 * reintroduce the mid-walk recompile freeze. All lamps are built once in the
 * FabScene constructor; visibility flips only inside the throttled
 * imperative tick. A dev watchdog below turns any violation into a loud
 * console error.
 *
 * NO R3F EVENT HANDLERS ON MESHES (no onClick/onPointer* props anywhere
 * under this canvas). Picking is centralized - semantic, nearest-geometry-
 * across-tools with trigger fallback - in FabScene.pick*, dispatched from
 * Player.onClick. Per-mesh handlers would make R3F raycast the whole
 * procedural hall on every pointermove.
 *
 * THE CLOCK: frameloop="never", driven through gl.setAnimationLoop, because
 * three.js swaps that hook onto the XR session's own requestAnimationFrame
 * while a headset is presenting - one loop serves desktop and VR. R3F's
 * advance(timestamp) expects SECONDS in never-mode (delta is derived from
 * the timestamps); both consumers additionally clamp dt to 0.1 s.
 */

import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { useEffect, useLayoutEffect, useRef } from 'react'
import * as THREE from 'three'
import { FabScene } from '../FabScene'
import { onHallChange } from '../hall'
import { BOOT_TIMING } from '../bootTiming'
import type { HmiScreens } from '../hmi'
import type { Player } from '../Player'
import { loadPeople } from '../people'
import { loadSubfabCrew } from '../subfabCrew'
import { loadChaseCrew } from '../chaseCrew'
import { FabPostFx } from '../postfx'
import { FabSound } from '../sound'
import { panelStore } from '../../app/panel/panelStore'
import { groundHeightAt } from '../subfabLayout'
import { onQualityChange, qualityFrame, resolvedImage } from '../quality'
import { VRMode } from '../../xr/VRMode'

export interface FabWorld {
  fab: FabScene
  /** The root scene the hall is built into, for rebuilding it in place. */
  scene: THREE.Scene
  vr: VRMode
  gl: THREE.WebGLRenderer
  /** The high tier's image pipeline. On the world for the dev handle only. */
  postfx: FabPostFx
}

export interface FabCanvasProps {
  player: Player
  screens: HmiScreens
  /** prefers-reduced-motion, read once at boot by the shell. */
  reducedMotion: boolean
  /** Has the student crossed the airlock? (walk-lock's entered flag.) */
  isEntered: () => boolean
  /** The shell's frame work - the frameBus's tick. Runs before the render. */
  onTick: (dt: number) => void
  /** Fires once the world exists and input is bound. May fire twice in dev
   *  (StrictMode re-runs effects); the shell guards. */
  onReady: (world: FabWorld) => void
}

function FabWorldRoot({
  player,
  screens,
  reducedMotion,
  isEntered,
  onTick,
  onReady,
}: FabCanvasProps) {
  const gl = useThree((s) => s.gl)
  const scene = useThree((s) => s.scene)
  const set = useThree((s) => s.set)
  const advance = useThree((s) => s.advance)

  // Constructed during the first render, ref-guarded: StrictMode invokes
  // render twice, and a constructor that scans the floor for light budgets
  // and captures an environment map must not run twice. The constructor adds
  // fab.group to the root scene itself, so there is no <primitive> for it.
  const worldRef = useRef<FabWorld | null>(null)
  if (!worldRef.current) {
    worldRef.current = {
      scene,
      fab: new FabScene(scene, screens, gl),
      vr: new VRMode(gl, player.yawObject, scene),
      gl,
      // The high tier's image pipeline (AO + bloom). Sized properly by the
      // size effect below; built here so its prewarm can share the splash.
      postfx: new FabPostFx(gl, scene, player.camera, 2, 2),
    }
  }
  const world = worldRef.current
  const postfx = world.postfx

  // Stable refs for the callbacks so the frame loop never chases identity.
  const onTickRef = useRef(onTick)
  onTickRef.current = onTick
  const isEnteredRef = useRef(isEntered)
  isEnteredRef.current = isEntered

  useLayoutEffect(() => {
    // The player's camera IS the camera R3F renders with. Note that adopting
    // it does NOT hand R3F responsibility for its aspect - see the effect
    // below, which is the half this originally got wrong.
    set({ camera: player.camera as unknown as THREE.PerspectiveCamera })
    gl.domElement.className = 'viewport'
    player.bindInput(gl.domElement)
    player.groundAt = groundHeightAt
    player.setColliders(world.fab.colliders())
    onReady(world)
    return () => {
      player.dispose()
    }
    // Mount-once wiring; every value here is session-stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Keep the camera's aspect on the canvas it actually draws into.
   *
   * R3F resizes only the camera it CREATED. A camera handed to it through
   * `set({ camera })` is treated as the caller's business, so this one kept
   * the 1 it was constructed with while the canvas went wide - and three.js
   * dutifully stretched a square projection across a 16:9 viewport. Every
   * horizontal distance in the hall came out about 1.9x too wide, which
   * reads exactly as the room being "stretched": the aisle too broad, the
   * tools at the frame edge smeared, the perspective too fast.
   *
   * The conversion dropped main.ts's resize() on the assumption that R3F
   * had it covered. It did not. This is that handler, in R3F's terms:
   * `size` is the measured CSS box, so it also fires on the synthetic
   * resize FabCanvas dispatches at mount for background tabs.
   */
  const size = useThree((s) => s.size)
  const setDpr = useThree((s) => s.setDpr)
  const sizeRef = useRef(size)
  sizeRef.current = size
  useLayoutEffect(() => {
    if (size.height === 0) return
    player.camera.aspect = size.width / size.height
    player.camera.updateProjectionMatrix()
    // The pipeline's targets follow the canvas the same way the camera does.
    postfx.setSize(size.width, size.height)
  }, [size.width, size.height, player.camera, postfx])

  // Prewarm BOTH tiers' shader programs, then start the loop - nothing
  // renders before the compile is paid, so neither the first frame nor the
  // governor's later promotion stalls a walking student.
  useLayoutEffect(() => {
    // ...including the image pipeline's off-screen variants under each
    // tier's light count, then the passes' own shaders.
    // The throwaway render also draws the shadow map, which is the only
    // way to compile the casters' depth programs: renderer.compile registers
    // the shadow-casting LIGHTS so materials get the right uniforms, but it
    // never touches a depth material. It runs under EACH tier - three keys
    // even a depth-only program by the visible light count - so a shadow
    // map first drawn on the high tier compiles the moment the governor
    // promotes, mid-walk, unless it was drawn here under that count.
    //
    // The box it draws is the student's own shadow scope, not the whole
    // hall. A depth program is per material VARIANT, not per object, and the
    // tools, the bulkhead, the hoist and the instanced carriers within reach
    // of any spawn cover the variants; what a hall-wide box added was the
    // first GPU upload of every geometry and all 276 textures in the
    // building, measured at two of the boot's five seconds. Those upload now
    // when first seen, a few milliseconds a bay, on the walk. A variant that
    // is only ever cast far from every spawn would compile on first sight;
    // none is known.
    const prewarmAll = (): void => {
      world.fab.prewarmLighting(player.camera, player.yawObject.position, () => {
        postfx.compileSceneVariants()
        if (BOOT_TIMING) performance.mark('boot:pre:postfx variants')
        postfx.compilePasses()
        if (BOOT_TIMING) performance.mark('boot:pre:passes render')
      })
      world.fab.setShadowScope('eye')
      if (import.meta.env.DEV) programBaseline = gl.info.programs?.length ?? 0
    }
    // The boot's prewarm, asynchronously: the driver links every program on
    // its own threads while the main thread stays free and the loading
    // screen keeps moving, and the loop starts the moment it resolves.
    // Everything below that would re-prewarm or draw waits for it, so the
    // tier never flips under a frame.
    prewarmAll()
    // The end of the compile, for the boot report in main.tsx. Kept
    // synchronous on purpose: measured against `compileAsync` on the
    // integrated GPU this project targets, the parallel path was ten times
    // slower per program - polling round-trips with no compile-time win -
    // and it stalled outright in a hidden tab. Blocking, and done, is the
    // better boot here.
    if (BOOT_TIMING) performance.mark('boot:compiled')
    let disposed = false
    // The technicians are the one loaded asset. They arrive after the hall
    // is built - behind the splash on any normal connection - and bring
    // skinned-mesh programs with them, so the prewarm runs once more when
    // they land, and the walk learns where they stand.
    const base = `${import.meta.env.BASE_URL}models/`
    void loadPeople({ f: `${base}technician_f.glb`, m: `${base}technician.glb` }).then((people) => {
      if (disposed || !people) return
      world.fab.addPeople(people)
      player.setColliders(world.fab.colliders())
      prewarmAll()
    })
    // The sub-fab crew: the same bodies, dressed for a room that is not a
    // cleanroom. Its own module, its own load, the same compile treatment.
    void loadSubfabCrew({ f: `${base}technician_f.glb`, m: `${base}technician.glb` }).then((crew) => {
      if (disposed || !crew) return
      world.fab.addSubfabCrew(crew)
      player.setColliders(world.fab.colliders())
      prewarmAll()
    })
    // The chase crew: gowned like the aisle's technicians, since the chase
    // is entered from the aisle without degowning. Same load, same compile.
    void loadChaseCrew({ f: `${base}technician_f.glb`, m: `${base}technician.glb` }).then((crew) => {
      if (disposed || !crew) return
      world.fab.addChaseCrew(crew)
      player.setColliders(world.fab.colliders())
      prewarmAll()
    })
    // The pipeline runs on the high tier only; a tier change flips a flag.
    //
    // THE TIER OWNS THE PIXEL RATIO. On a 200% display the canvas used to
    // follow the device and draw four times the pixels of a 1:1 canvas,
    // each shaded under the full lamp budget - 30 fps on the integrated
    // GPU this project promises to run on, at 1:1 it had held 60. The lean
    // tier draws 1:1; the high tier, which a machine only reaches by
    // holding 55 fps at 1:1, may go to 1.5. (Measured in my own tab at
    // ratio 1, the shortfall never showed - the student's window is what
    // counts.)
    const applyTier = (): void => {
      // The light budget and the image pipeline are separate rungs now: a
      // machine can hold the fuller ceiling without holding AO.
      const image = resolvedImage()
      postfx.setEnabled(image)
      const ratio = image ? Math.min(window.devicePixelRatio, 1.5) : 1
      // On the renderer now (R3F's own setDpr lands a render later), and
      // in R3F's state so its resize handling agrees.
      gl.setPixelRatio(ratio)
      setDpr(ratio)
      // The pipeline's targets follow the ratio the same way they follow
      // the canvas size.
      const s = sizeRef.current
      if (s.height > 0) postfx.setSize(s.width, s.height)
    }
    applyTier()
    const offQuality = onQualityChange(applyTier)
    // The hall's sound starts on the first gesture, as a browser requires;
    // M mutes it, and the mute is remembered.
    const sound = new FabSound()
    soundRef.current = sound
    const startSound = (): void => sound.start()
    const onKey = (e: KeyboardEvent): void => {
      if (e.code === 'KeyM' && !e.repeat && !(e.target instanceof HTMLInputElement)) sound.toggle()
    }
    window.addEventListener('pointerdown', startSound)
    window.addEventListener('keydown', startSound)
    window.addEventListener('keydown', onKey)
    // The pause follows the panel store directly rather than the frame
    // loop: a panel opened while no frame runs (a hidden tab, a stalled
    // loop) was audible until the next frame happened to ask.
    const followPanel = (): void => sound.setPaused(panelStore.open !== null)
    const offPanel = panelStore.subscribe(followPanel)
    followPanel()
    if (import.meta.env.DEV) (window as unknown as Record<string, unknown>).__simfabSound = sound
    gl.setAnimationLoop((t) => advance(t / 1000, true))
    // The sub-fab, behind the first frame. Its lamps have been in the budget
    // from the start (subfab.ts buildSubfabLamps); the room's geometry is
    // what took the time, and nobody spawning on the hall floor can see it.
    // On the first idle moment - or two seconds, whichever is first, since
    // a student CAN reach the stairwell - it is built, the walk's colliders
    // are refreshed, and the prewarm runs once more so its programs compile
    // now and not on the first step down. A no-op when the composition root
    // already built it for a place below the slab.
    const buildSubfabLater = (): void => {
      if (disposed) return
      const t0 = performance.now()
      if (!world.fab.ensureSubfab()) return
      const t1 = performance.now()
      player.setColliders(world.fab.colliders())
      const t2 = performance.now()
      // The room's programs only - the full prewarm over the whole hall is
      // what the GLB arrivals pay, and it is seconds; this is milliseconds.
      world.fab.prewarmSubfab(player.camera, player.yawObject.position)
      if (import.meta.env.DEV) {
        // The room's programs are new on purpose; the growth check starts over.
        programBaseline = gl.info.programs?.length ?? 0
        const ms = (a: number, b: number) => Math.round(b - a) + ' ms'
        console.info('[boot:deferred] subfab build ' + ms(t0, t1) + ' | colliders ' + ms(t1, t2) + ' | compile ' + ms(t2, performance.now()))
      }
    }
    if ('requestIdleCallback' in window) window.requestIdleCallback(buildSubfabLater, { timeout: 2000 })
    else setTimeout(buildSubfabLater, 300)
    // A hall switch compiles the other layout's variants on purpose; the
    // growth check starts over from there. Registered after the composition
    // root's own listener, so it sees the count after the rebuild.
    const offHall = onHallChange(() => {
      if (import.meta.env.DEV) programBaseline = gl.info.programs?.length ?? 0
    })
    return () => {
      disposed = true
      offHall()
      offQuality()
      window.removeEventListener('pointerdown', startSound)
      window.removeEventListener('keydown', startSound)
      window.removeEventListener('keydown', onKey)
      offPanel()
      sound.dispose()
      gl.setAnimationLoop(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const soundRef = useRef<FabSound | null>(null)

  // Walk / VR / governor - first thing every frame.
  useFrame((state, delta) => {
    const dt = Math.min(delta, 0.1)
    const vr = world.vr
    // Feed the graphics-quality governor - but not from VR frames: a headset
    // runs at its own refresh rate and would skew the fps verdict.
    if (!vr.active) qualityFrame(dt)

    if (vr.active) {
      // In VR the headset owns the camera, so the desktop walk controller
      // stands down and the hand controllers drive the rig instead. The
      // rig's own y IS the ground in VR (the headset adds eye height on
      // top), so it doubles as the level for collision filtering and
      // follows the stair on the way down.
      vr.update(dt, (p) => player.resolveCollisions(p, player.yawObject.position.y))
      player.yawObject.position.y = groundHeightAt(
        player.yawObject.position.x,
        player.yawObject.position.z,
        player.yawObject.position.y,
      )
      player.setFoot(player.yawObject.position.y)
    } else if (isEnteredRef.current() || reducedMotion) {
      const t0 = performance.now()
      player.update(dt)
      frameTimes.walk += performance.now() - t0
    } else {
      // Before entry the camera flies itself, so the splash sits over a room
      // that is moving rather than over a still.
      player.idleDrift(dt)
    }
    // The shadow box travels with the eye; the working parts turn. Motion
    // in the room is held back for a student who asked the OS for less of it.
    const t1 = performance.now()
    world.fab.followEye(player.yawObject.position)
    // A tool panel open is the student's attention elsewhere: the room
    // holds still behind it and goes quiet, and picks up where it was when
    // the panel closes. (The solvers keep running - a process started in a
    // panel is the panel's business, not the room's.)
    const paused = panelStore.open !== null
    if (!reducedMotion && !paused) world.fab.animate(dt, player.yawObject.position)
    const t2 = performance.now()
    frameTimes.animate += t2 - t1
    const eye = player.yawObject.position
    soundRef.current?.update(eye, world.fab.ambience(eye), state.clock.elapsedTime)
    frameTimes.sound += performance.now() - t2
    frameTimes.frame += delta * 1000
    frameTimes.n++
  }, -100)

  // The shell's work - sim tick, scene pushes, HUD refs - then the render.
  useFrame((_, delta) => {
    const t0 = performance.now()
    onTickRef.current(Math.min(delta, 0.1))
    frameTimes.tick += performance.now() - t0
    if (import.meta.env.DEV) watchPrograms(gl)
  }, -90)

  // THE RENDER. A positive priority tells R3F to stand down from its own
  // gl.render, and the pipeline decides: straight to the screen on the lean
  // tier and in VR (byte-for-byte the frame R3F would have drawn), through
  // AO and bloom on the high tier.
  useFrame(() => {
    const t0 = performance.now()
    postfx.render(scene, player.camera, world.vr.active)
    frameTimes.render += performance.now() - t0
  }, 1)

  return <primitive object={player.yawObject} />
}

// ---------------------------------------------------- frame-time ledger
//
// Where a frame's main-thread time goes, by stage, so a slow machine can be
// asked rather than guessed at: `__simfabTimes()` in the console prints the
// per-frame averages since the last call, in milliseconds. `render` is the
// CPU cost of submitting the draw calls, not the GPU's time; a GPU-bound
// frame shows a `frame` total far above the sum of the stages.
const frameTimes = { walk: 0, animate: 0, sound: 0, tick: 0, render: 0, frame: 0, n: 0 }
if (typeof window !== 'undefined') {
  ;(window as unknown as Record<string, unknown>).__simfabTimes = () => {
    const n = Math.max(1, frameTimes.n)
    const out = {
      frames: frameTimes.n,
      frameMs: +(frameTimes.frame / n).toFixed(2),
      walkMs: +(frameTimes.walk / n).toFixed(2),
      animateMs: +(frameTimes.animate / n).toFixed(2),
      soundMs: +(frameTimes.sound / n).toFixed(2),
      tickMs: +(frameTimes.tick / n).toFixed(2),
      renderSubmitMs: +(frameTimes.render / n).toFixed(2),
    }
    for (const k of Object.keys(frameTimes) as Array<keyof typeof frameTimes>) frameTimes[k] = 0
    return out
  }
}

// ------------------------------------------------- dev recompile watchdog

/**
 * Programs are compiled at prewarm and never again - any growth mid-session
 * means something changed a shader hash (a light mounted, a first material
 * map assigned) and the next student to walk past it will hitch. Make that
 * loud in development instead of mysterious in a classroom.
 */
let programBaseline = 0
let programWarned = false
let watchAccumulator = 0
let lastWatch = 0

function watchPrograms(gl: THREE.WebGLRenderer): void {
  const now = performance.now()
  watchAccumulator += now - lastWatch
  lastWatch = now
  if (watchAccumulator < 1000) return
  watchAccumulator = 0
  const programs = gl.info.programs ?? []
  const count = programs.length
  if (count > programBaseline) {
    if (!programWarned) {
      programWarned = true
      // Name the culprits: the list is append-only, so everything past the
      // baseline is what compiled late.
      // `name` is the material's own name, blank for built-ins; the cache
      // key opens with the shader id (standard, depth, ...) or, for a
      // ShaderMaterial, its source - either way enough to recognise it.
      const late = programs
        .slice(programBaseline, programBaseline + 8)
        .map((p) => p.name || p.cacheKey.slice(0, 80))
      console.error(
        `Shader programs grew after prewarm (${programBaseline} -> ${count}): ` +
          'something changed a program hash mid-session (new light? first material.map?). ' +
          'This recompiles synchronously and will hitch a walking student. ' +
          `Late: ${late.join(', ')}`,
      )
    }
    programBaseline = count
  }
}

export function FabCanvas(props: FabCanvasProps) {
  // R3F measures its container only from ResizeObserver and window-resize
  // events, and a browser delivers neither to a hidden tab - so a page
  // opened in a background tab would defer the whole world (splash
  // included) until first shown. One synthetic resize after mount forces
  // the initial measure; layout is computable even while hidden. A PASSIVE
  // effect on purpose: the measure hook attaches its window listener in its
  // own passive effect, which runs before this parent one - a layout-effect
  // dispatch would fire before anyone is listening.
  useEffect(() => {
    window.dispatchEvent(new Event('resize'))
  }, [])
  return (
    <Canvas
      frameloop="never"
      gl={{ antialias: true, powerPreference: 'high-performance' }}
      dpr={1}
      shadows="soft"
      onCreated={({ gl }) => {
        gl.outputColorSpace = THREE.SRGBColorSpace
        gl.toneMapping = THREE.ACESFilmicToneMapping
        gl.toneMappingExposure = 0.86
      }}
      style={{ position: 'fixed', inset: 0 }}
    >
      <FabWorldRoot {...props} />
    </Canvas>
  )
}
